import { SyntheticEvent, useCallback, useState } from "react"
import {
  Block,
  Button,
  ButtonIcon,
  Settings03,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { Form, FormikProps, withFormik } from "formik"

import arrowsIcon from "#/assets/arrow-left-right.svg"
import {
  RootState,
  selectTokenBalance,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import {
  SwapFormField,
  SwapFormValue,
  SwapFromFieldData,
  SwappableToken,
  SwapSettingsView,
} from "#/shared/types"
import { floatToBigInt, validateMaximumTokenAmount } from "#/shared/utils"

import SwapModalAssetSelector from "./SwapModalAssetSelector"
import SwapModalInput from "./SwapModalInput"
import SwapModalRate from "./SwapModalRate"
import SwapModalSlippage from "./SwapModalSlippage"

type SwapModalFormProps = {
  values: SwapFormValue
  onSubmitForm: (values: SwapFormValue) => void
}

function SwapModalFormBase({
  handleSubmit,
  isValid,
  getFieldMeta,
  getFieldHelpers,
}: FormikProps<SwapFormValue>) {
  const [currentField, setCurrentField] = useState<SwapFormField | null>(null)
  const [currentToken, setCurrentToken] = useState<SwappableToken | null>(null)

  const { value: sellToken } = getFieldMeta<SwapFromFieldData>("sellToken")
  const { value: buyToken } = getFieldMeta<SwapFromFieldData>("buyToken")
  const { value: slippage } = getFieldMeta<string>("slippage")

  const { setValue: setSellTokenField } =
    getFieldHelpers<SwapFromFieldData>("sellToken")

  const { setValue: setBuyTokenField } =
    getFieldHelpers<SwapFromFieldData>("buyToken")

  const { setValue: setSlippage } = getFieldHelpers<string>("slippage")

  const sellTokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, sellToken.token),
  )

  const sellTokenBalance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, sellToken.token),
  )

  const isLowerOrEqualSellTokenBalance = validateMaximumTokenAmount(
    floatToBigInt(sellToken.amount, sellTokenDecimals),
    sellTokenBalance,
  )

  const [css, theme] = useStyletron()

  const completedData =
    !!sellToken.amount &&
    !!sellToken.token &&
    !!buyToken.amount &&
    !!buyToken.token

  const isDisabled =
    !completedData || !isLowerOrEqualSellTokenBalance || !isValid

  const handleSwitchTokens = useCallback(() => {
    setSellTokenField({ amount: sellToken.amount, token: buyToken.token })
    setBuyTokenField({ amount: buyToken.amount, token: sellToken.token })
  }, [buyToken, sellToken, setSellTokenField, setBuyTokenField])

  const [currentSettingsMenu, setCurrentSettingsMenu] =
    useState<SwapSettingsView | null>(null)

  const slippageSettingsHandler = useCallback(() => {
    setCurrentSettingsMenu(SwapSettingsView.SLIPPAGE)
  }, [])

  const openAssetSelectorHandler = useCallback(
    (field: SwapFormField) => {
      setCurrentSettingsMenu(SwapSettingsView.ASSET_SELECTOR)
      setCurrentField(field)

      setCurrentToken(
        field === SwapFormField.BUY_TOKEN ? buyToken.token : sellToken.token,
      )
    },
    [buyToken.token, sellToken.token],
  )

  const updateSelectedToken = useCallback(
    (token: SwappableToken) => {
      if (!currentField) return

      if (currentField === SwapFormField.BUY_TOKEN) {
        setBuyTokenField({ amount: buyToken.amount, token })
      } else {
        setSellTokenField({ amount: sellToken.amount, token })
      }
    },
    [
      currentField,
      setBuyTokenField,
      setSellTokenField,
      buyToken.amount,
      sellToken.amount,
    ],
  )

  const closeSettingsMenu = useCallback(
    (e: SyntheticEvent<HTMLButtonElement>) => {
      e.preventDefault()
      setCurrentSettingsMenu(null)
    },
    [],
  )

  return (
    <Form onSubmit={handleSubmit}>
      <Block
        className={css({
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: theme.sizing.scale200,
        })}
      >
        <ButtonIcon
          kind="secondary"
          size="small"
          onClick={slippageSettingsHandler}
          type="button"
        >
          {/* TODO: Replace with Settings04 */}
          <Settings03 color={theme.colors.black} />
        </ButtonIcon>
      </Block>
      <Block
        className={css({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: theme.sizing.scale200,
        })}
      >
        <SwapModalInput
          label="From"
          fieldName={SwapFormField.SELL_TOKEN}
          onOpenAssetSelector={() => {
            openAssetSelectorHandler(SwapFormField.SELL_TOKEN)
          }}
        />
        <ButtonIcon type="button" size="small" onClick={handleSwitchTokens}>
          {/* TODO: Replace with SwitchVertical02 icon */}
          <Block
            as="img"
            src={arrowsIcon}
            className={css({ transform: "rotate(90deg)" })}
          />
        </ButtonIcon>
        <SwapModalInput
          label="To"
          fieldName={SwapFormField.BUY_TOKEN}
          onOpenAssetSelector={() => {
            openAssetSelectorHandler(SwapFormField.BUY_TOKEN)
          }}
        />
        <SwapModalRate sellToken={sellToken.token} buyToken={buyToken.token} />
        <Button
          size="large"
          type="submit"
          disabled={isDisabled}
          overrides={{ BaseButton: { style: () => ({ width: "100%" }) } }}
        >
          Review
        </Button>
      </Block>
      <SwapModalSlippage
        isOpen={currentSettingsMenu === SwapSettingsView.SLIPPAGE}
        slippage={slippage}
        setSlippage={setSlippage}
        onClose={closeSettingsMenu}
      />
      <SwapModalAssetSelector
        isOpen={currentSettingsMenu === SwapSettingsView.ASSET_SELECTOR}
        currentToken={currentToken}
        onTokenSelect={updateSelectedToken}
        onClose={closeSettingsMenu}
      />
    </Form>
  )
}

const SwapModalForm = withFormik<SwapModalFormProps, SwapFormValue>({
  mapPropsToValues: ({ values }) => values,
  handleSubmit: (values, { props }) => props.onSubmitForm(values),
  // Validation is done at the asset input level
  validateOnBlur: false,
  validateOnChange: false,
  validateOnMount: false,
})(SwapModalFormBase)

export default SwapModalForm
